import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import EmailInput from '../ui/EmailInput';
import Disclaimer from '../ui/Disclaimer';
import { useHubspot } from '../../hooks/useHubspot';
import { validateEmail } from '../../utils/validation';
import { suggestEmailDomain } from '../../utils/email-domain-suggestions';
import { marketingParams } from '../../utils/marketingParams';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

type EmailFormProps = {
  formData: FormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSuggestionAccept: (email: string) => void;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function EmailForm({ formData, onChange, onSuggestionAccept, onSubmit }: EmailFormProps) {
  const { t } = useTranslation('wizard');
  const [error, setError] = useState<string | null>(null);
  const [submitSuggestion, setSubmitSuggestion] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const hubspot = useHubspot();
  const params = marketingParams.getParams();
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();
  
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    const validationError = emailValue ? validateEmail(emailValue) : null;
    setError(validationError);
    setSubmitSuggestion(null);
    onChange(e);
  };

  const handleSuggestionAccept = (suggestedEmail: string) => {
    setError(null);
    setSubmitSuggestion(null);
    onSuggestionAccept(suggestedEmail);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError(t('email.requiredError'));
      return;
    }

    const validationError = validateEmail(formData.email);
    if (validationError) {
      setError(validationError);
      return;
    }

    // On the first submit attempt, surface a domain suggestion if one exists.
    // If the user clicks Continue a second time while the suggestion is visible,
    // submitSuggestion is already non-null so we skip this block and proceed.
    if (submitSuggestion === null) {
      const suggestion = suggestEmailDomain(formData.email);
      if (suggestion) {
        setSubmitSuggestion(suggestion);
        return;
      }
    }

    setSubmitSuggestion(null);

    try {
      hubspot.identify({
        email: formData.email,
        ...params,
      });

      hubspot.trackEvent('email_step_completed', {
        email: formData.email,
        form_step: 'email',
        ...params
      });

      onSubmit({ email: formData.email });
    } catch (err) {
      console.error('Failed to submit HubSpot form:', err);
      onSubmit({ email: formData.email });
    }
  };

  const isButtonDisabled = !formData.email || !!error;

  return (
    <form id="email-form" name="email-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <Title 
          id="email-title"
          name="email-title"
          as="h1" 
          className="mb-2"
        >
          {t('email.title')}
        </Title>
        <Description 
          id="email-description"
          name="email-description"
          size="lg"
        >
          {t('email.description')}
        </Description>
      </div>

      <EmailInput
        ref={emailInputRef}
        value={formData.email}
        onChange={handleEmailChange}
        onSuggestionAccept={handleSuggestionAccept}
        externalSuggestion={submitSuggestion}
        error={error}
      />

      {Object.entries(params).map(([key, value]) => (
        <input
          key={key}
          type="hidden"
          name={key}
          value={value}
          data-hs-field-hidden="true"
        />
      ))}

      <Button
        id="email-submit"
        name="email-submit"
        type="submit"
        fullWidth
        size="lg"
        disabled={isButtonDisabled}
      >
        {t('email.submit')}
      </Button>

      {showDisclaimer && (
        <Disclaimer
          id="email-general-disclaimer"
          name="email-general-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </form>
  );
}
